<template>
  <div class="interrupt-block">
    <div v-for="interrupt in chunk.data" :key="interrupt.id">
      <!-- Submitted: done pill matching ToolCallBlockSSE done state -->
      <div v-if="getSubmitted(interrupt).submitted" class="interrupt-block__pill">
        <span
          class="chunk-status-icon"
          :class="getSubmitted(interrupt).decision === 'reject' ? 'chunk-status-icon--error' : 'chunk-status-icon--done'"
        >
          <BbIcon size="small" :name="getSubmitted(interrupt).decision === 'reject' ? 'x-small' : 'checkmark-small'" />
        </span>
        <span class="interrupt-block__pill-label">
          {{
            interrupt.value.type === 'imageStyleChoicesRequest'
              ? t('conversation.imageParams.imageGeneration')
              : formatKey(interrupt.value.action_requests?.[0]?.name ?? '')
          }}
          <span class="interrupt-block__pill-decision">· {{ getSubmitted(interrupt).decision }}</span>
        </span>
      </div>

      <!-- Pending: expansion panel matching ToolCallBlockSSE open state -->
      <v-expansion-panels v-else flat model-value="0">
        <v-expansion-panel value="0">
          <v-expansion-panel-title>
            <div class="interrupt-block__title">
              <span class="interrupt-block__title-label"> Arcane needs your approval before it can continue: </span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <ImageStyleChoiceInterrupt
              v-if="interrupt.value.type === 'imageStyleChoicesRequest'"
              @submitted="markSubmitted(interrupt.id, 'submitted')"
            />
            <HitlReviewInterrupt v-else :interrupt="interrupt" @submitted="markSubmitted(interrupt.id, $event)" />
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue';
import { useI18n } from 'vue-i18n';

import { formatKey } from '~/utils/format';

import ImageStyleChoiceInterrupt from './ImageStyleChoiceInterrupt.vue';
import HitlReviewInterrupt from './HitlReviewInterrupt.vue';

export default {
  name: 'InterruptBlockSSE',
  components: { ImageStyleChoiceInterrupt, HitlReviewInterrupt },
  props: {
    chunk: {
      type: Object,
      required: true,
    },
  },
  setup() {
    const { t } = useI18n();
    const submittedState = reactive({});

    const getSubmitted = (interrupt) => {
      if (!submittedState[interrupt.id]) {
        submittedState[interrupt.id] = interrupt.resolvedDecision
          ? { submitted: true, decision: interrupt.resolvedDecision }
          : { submitted: false, decision: '' };
      }
      return submittedState[interrupt.id];
    };

    const markSubmitted = (id, decision) => {
      const state = submittedState[id] || (submittedState[id] = { submitted: false, decision: '' });
      state.submitted = true;
      state.decision = decision;
    };

    return {
      t,
      formatKey,
      getSubmitted,
      markSubmitted,
    };
  },
};
</script>

<style scoped lang="scss">
.interrupt-block {
  display: flex;
  flex-direction: column;
  gap: $bb-spacing-2;
}

// Done pill — mirrors ToolCallBlockSSE --done state
.interrupt-block__pill {
  display: inline-flex;
  align-items: center;
  gap: $bb-spacing-2;
  background-color: $bb-neutral-6;
  border-radius: 100px;
  padding: $bb-spacing-2;
  width: fit-content;
}

.interrupt-block__pill-label {
  font-size: 12px;
  font-weight: 500;
  color: $bb-neutral-1;
  line-height: 16px;
}

.interrupt-block__pill-decision {
  color: $bb-neutral-3;
  font-weight: 400;
}

// Shared icon styles (mirrors ToolCallTitle / ToolStepRow)
.chunk-status-icon {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 16px;
  height: 16px;
}
.chunk-status-icon--done {
  background-color: #000;
  color: $bb-neutral-0 !important;
}
.chunk-status-icon--error {
  background: #fee2e2;
  color: $bb-interface-5 !important;
}

// Expansion panel — mirrors ToolCallBlockSSE open state
:deep(.v-expansion-panel) {
  background-color: $bb-neutral-6;
  border-radius: 16px !important;

  .v-expansion-panel__shadow {
    box-shadow: none;
  }

  .v-expansion-panel-title {
    min-height: unset;
    padding: $bb-spacing-2 $bb-spacing-3;

    .v-expansion-panel-title__overlay {
      background-color: $bb-neutral-6;
    }
  }

  .v-expansion-panel-text .v-expansion-panel-text__wrapper {
    padding: $bb-spacing-2 $bb-spacing-6 $bb-spacing-4 !important;
    display: flex;
    flex-direction: column;
    gap: $bb-spacing-3;
  }
}

.interrupt-block__title {
  display: flex;
  align-items: center;
  gap: $bb-spacing-2;
  flex: 1 1 auto;
}

.interrupt-block__title-label {
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
}
</style>
